import { useState, useEffect } from 'react';
import { CheckCircleIcon, XCircleIcon, ArrowDownTrayIcon, EyeIcon } from '@heroicons/react/24/outline';
import api from '../services/api';
import confetti from 'canvas-confetti';

export default function Certificate() {
  const [eligibility, setEligibility] = useState(null);
  const [certificates, setCertificates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      const [eligibilityRes, certificatesRes] = await Promise.all([
        api.get('/certificates/eligibility'),
        api.get('/certificates')
      ]);
      setEligibility(eligibilityRes.data);
      setCertificates(certificatesRes.data.certificates);
    } catch (error) {
      console.error('Error fetching certificate data:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateCertificate = async () => {
    try {
      setGenerating(true);
      const response = await api.post('/certificates/generate');
      
      // Fire confetti!
      confetti({
        particleCount: 200,
        spread: 70,
        origin: { y: 0.6 }
      });

      // Refresh data
      await fetchData();
      
      alert('🎉 Certificate generated successfully!');
    } catch (error) {
      console.error('Error generating certificate:', error);
      alert(error.response?.data?.message || 'Failed to generate certificate');
    } finally {
      setGenerating(false);
    }
  };

  const downloadCertificate = async (certificateId) => {
    try {
      const response = await api.get(`/certificates/${certificateId}/download`, {
        responseType: 'blob'
      });
      
      // Create blob link to download
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `certificate-${certificateId}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.parentNode.removeChild(link);
    } catch (error) {
      console.error('Error downloading certificate:', error);
      alert('Failed to download certificate');
    }
  };

  const viewCertificate = async (certificateId) => {
    try {
      // Download the PDF as a blob
      const response = await api.get(`/certificates/${certificateId}/stream`, {
        responseType: 'blob'
      });
      
      // Create a blob URL and open in new tab
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      
      // Note: URL is not revoked to allow the PDF to load in the new tab
    } catch (error) {
      console.error('Error viewing certificate:', error);
      alert('Failed to view certificate');
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-cte-blue"></div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-3xl font-bold text-cte-black mb-6">Your Certificate</h1>

      {/* Eligibility Status */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 mb-6">
        <h2 className="text-xl font-semibold text-gray-900 mb-4">Certificate Eligibility</h2>
        
        <div className="space-y-4">
          {/* Overall Status */}
          <div className={`flex items-center gap-3 p-4 rounded-lg ${eligibility?.eligible ? 'bg-green-50' : 'bg-yellow-50'}`}>
            {eligibility?.eligible ? (
              <>
                <CheckCircleIcon className="w-6 h-6 text-green-600" />
                <span className="text-green-800 font-medium">You are eligible for a certificate! 🎉</span>
              </>
            ) : (
              <>
                <XCircleIcon className="w-6 h-6 text-yellow-600" />
                <span className="text-yellow-800 font-medium">Complete all requirements to earn your certificate</span>
              </>
            )}
          </div>

          {/* Requirements */}
          <div className="grid md:grid-cols-3 gap-4">
            {eligibility?.requirements && (
              <>
                <RequirementCard
                  title="Modules"
                  completed={eligibility.requirements.modules.completed}
                  required={eligibility.requirements.modules.required}
                  met={eligibility.requirements.modules.met}
                />
                <RequirementCard
                  title="Labs"
                  completed={eligibility.requirements.labs.completed}
                  required={eligibility.requirements.labs.required}
                  met={eligibility.requirements.labs.met}
                />
                <RequirementCard
                  title="Capstones"
                  completed={eligibility.requirements.capstones.completed}
                  required={eligibility.requirements.capstones.required}
                  met={eligibility.requirements.capstones.met}
                />
              </>
            )}
          </div>

          {/* Generate Button */}
          {eligibility?.eligible && certificates.length === 0 && (
            <button
              onClick={generateCertificate}
              disabled={generating}
              className="w-full bg-cte-blue text-white px-6 py-3 rounded-lg font-semibold hover:bg-blue-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {generating ? 'Generating...' : 'Generate My Certificate'}
            </button>
          )}
        </div>
      </div>

      {/* Existing Certificates */}
      {certificates.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">Your Certificates</h2>
          
          <div className="space-y-4">
            {certificates.map((cert) => (
              <div key={cert.id} className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow">
                <div className="flex items-start justify-between">
                  <div className="flex-1">
                    <h3 className="font-semibold text-lg text-gray-900">{cert.course_name}</h3>
                    <p className="text-sm text-gray-500 mt-1">
                      Certificate #: {cert.certificate_number}
                    </p>
                    <p className="text-sm text-gray-500">
                      Issued: {new Date(cert.issued_at).toLocaleDateString()}
                    </p>
                    <p className="text-sm text-gray-500">
                      Completion Time: {cert.completion_time_hours} hours
                    </p>
                  </div>

                  <div className="flex gap-2">
                    <button
                      onClick={() => viewCertificate(cert.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200 transition-colors"
                    >
                      <EyeIcon className="w-5 h-5" />
                      View
                    </button>
                    <button
                      onClick={() => downloadCertificate(cert.id)}
                      className="flex items-center gap-2 px-4 py-2 bg-cte-blue text-white rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      <ArrowDownTrayIcon className="w-5 h-5" />
                      Download
                    </button>
                  </div>
                </div>

                {cert.verification_url && (
                  <div className="mt-3 pt-3 border-t border-gray-200">
                    <p className="text-sm text-gray-600">
                      Verification URL:{' '}
                      <a 
                        href={cert.verification_url} 
                        target="_blank" 
                        rel="noopener noreferrer"
                        className="text-cte-blue hover:underline"
                      >
                        {cert.verification_url}
                      </a>
                    </p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function RequirementCard({ title, completed, required, met }) {
  return (
    <div className={`p-4 rounded-lg border-2 ${met ? 'border-green-500 bg-green-50' : 'border-gray-300 bg-gray-50'}`}>
      <div className="flex items-center justify-between mb-2">
        <span className="font-medium text-gray-900">{title}</span>
        {met ? (
          <CheckCircleIcon className="w-5 h-5 text-green-600" />
        ) : (
          <XCircleIcon className="w-5 h-5 text-gray-400" />
        )}
      </div>
      <div className="text-2xl font-bold text-gray-900">
        {completed} / {required}
      </div>
      <div className="w-full bg-gray-200 rounded-full h-2 mt-2">
        <div
          className={`h-2 rounded-full transition-all ${met ? 'bg-green-500' : 'bg-gray-400'}`}
          style={{ width: `${(completed / required) * 100}%` }}
        ></div>
      </div>
    </div>
  );
}
